"use client"

/* ── The "an MRI sees what your blood can't" beat (§7b), pinned scroll-reveal —
   ported from the metabolism LP (MetabolismMriImmersive) with the hormones
   findings. Two overlapping layers, each bounded to the pinned stage: at rest
   the heading sits centred; on scroll it rides up and out while three finding
   cards fade in and take the whole stage. Below md it degrades to a static
   stack.

   The three findings are the structural conditions a dedicated contrast-free
   pelvic sequence shows that blood can't: uterine fibroids, ovarian cysts and
   ovarian anatomy, and DEEP endometriosis + adenomyosis. Guardrails from the
   verification deck: always say "deep" endometriosis (superficial disease can
   be invisible to imaging, so the scan never "rules endometriosis out"), and
   the scan screens for a PCOS work-up — it never "diagnoses PCOS". Chip
   findings are illustrative examples (we never diagnose). ── */

import Image from "next/image"
import { useState, useEffect, useRef, useCallback } from "react"
import { useTranslations } from "@/lib/intl/use-translations"
import { goKeys as k } from "@/i18n/go-keys"
import { ArrowRightIcon } from "@/components/ui/button"
import { blobMediaUrl } from "@/lib/blob-url"
import { ReadingChip, type Finding } from "./female-hormones-reading-chip"
import { SECTION } from "./female-hormones-components"

/* ── Finding-card imagery — Ahead-generated reading-room composites: a clinician
   reviewing an illustrative pelvic MRI on a workstation screen (the screen is the
   focus, the clinician secondary). Warm, non-graphic framing chosen deliberately
   over the raw scan crops these replaced. The scans are ILLUSTRATIVE, not a real
   patient's study — never present them as a diagnosis; the chips are examples.
   Ahead-owned assets (no third-party licence/attribution).
     • fibroids    — mri-screen-fibroids.jpg      (axial, discrete rounded mass)
     • ovarian     — mri-screen-ovarian-cysts.jpg (axial, uterus + adnexa)
     • adenomyosis — mri-screen-adenomyosis.jpg   (sagittal, heterogeneous uterus) ── */

export function FemaleHormonesMriImmersive() {
  const t = useTranslations("go")
  const trackRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)

  // The three structural findings, with illustrative chip findings (severity
  // dots like the report's summary pills, not measured blood values).
  const findings = [
    {
      titleKey: k.femaleHormonesMriFinding1Title,
      noteKey: k.femaleHormonesMriFinding1Note,
      image: blobMediaUrl("/images/marketing/mri-screen-fibroids-v3.jpg"),
      imgClass: "",
      chip: [
        {
          count: 1,
          label: t(k.femaleHormonesMriBadgeObservation),
          tone: "green",
        },
      ] as Finding[],
    },
    {
      titleKey: k.femaleHormonesMriFinding2Title,
      noteKey: k.femaleHormonesMriFinding2Note,
      image: blobMediaUrl("/images/marketing/mri-screen-ovarian-cysts-v3.jpg"),
      imgClass: "",
      chip: [
        {
          count: 2,
          label: t(k.femaleHormonesMriBadgeObservation),
          tone: "green",
        },
      ] as Finding[],
    },
    {
      titleKey: k.femaleHormonesMriFinding3Title,
      noteKey: k.femaleHormonesMriFinding3Note,
      image: blobMediaUrl("/images/marketing/mri-screen-adenomyosis-v3.jpg"),
      imgClass: "",
      chip: [
        { count: 1, label: t(k.femaleHormonesMriBadgeMinor), tone: "olive" },
      ] as Finding[],
    },
  ]

  // SSR + first client render pin (desktop-first); a phone flips to the static
  // stack after mount. Same-value initial render (true), so no hydration
  // mismatch.
  //
  // This used to also carry `and (min-height: 760px)`, which dropped short
  // laptops to the static stack because the pinned stage clipped its content.
  // The stage now bounds and scales its own content (see below), so the gate
  // no longer buys anything — it only denied the pinned reveal to short
  // viewports and left this page behaving differently from its two sibling
  // LPs, which never had it.
  const [pinned, setPinned] = useState(true)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => setPinned(mq.matches)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Reveal window (fraction of the pinned track the reveal plays over).
  const REVEAL_START = 0.08
  const REVEAL_END = 0.95

  const applyProgress = useCallback((p: number) => {
    const stage = stageRef.current
    if (!stage) return
    const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v))
    const smooth = (x: number) => x * x * (3 - 2 * x)
    // Reveal progress across the pinned track.
    const rl = clamp((p - REVEAL_START) / (REVEAL_END - REVEAL_START))
    // Blur cross-fade: the photo is sharp at rest, then blurs EARLY (before the
    // cards). --sharp 1→0 fades the sharp layer out over the blurred one.
    const blurP = smooth(clamp((p - 0.08) / 0.22))
    // Cards fade in once the header is on its way out.
    const op = smooth(clamp((rl - 0.3) / 0.62))
    // The header leaves in two beats rather than stopping all at once: the
    // eyebrow + body line go first (--sub), then the heading (--head). --head
    // also means the heading is invisible well before the travel ends, so it is
    // never guillotined at full opacity by the stage's overflow-hidden edge.
    const sub = 1 - smooth(clamp(rl / 0.28))
    const head = 1 - smooth(clamp(rl / 0.42))
    stage.style.setProperty("--reveal", `${rl}`)
    stage.style.setProperty("--sharp", `${1 - blurP}`)
    stage.style.setProperty("--op", `${op}`)
    stage.style.setProperty("--sub", `${sub}`)
    stage.style.setProperty("--head", `${head}`)
    // The cards layer covers the whole stage, so it must not swallow clicks
    // while it is still transparent.
    stage.style.setProperty("--cards-pe", op > 0.5 ? "auto" : "none")
  }, [])

  const applyFromScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    const total = track.offsetHeight - window.innerHeight
    const p =
      total > 0
        ? Math.min(1, Math.max(0, -track.getBoundingClientRect().top / total))
        : 0
    applyProgress(p)
  }, [applyProgress])

  useEffect(() => {
    if (!pinned) return
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    if (reduce) {
      applyProgress(1)
      return
    }
    let raf = 0
    const onScroll = () => {
      if (!raf)
        raf = requestAnimationFrame(() => {
          raf = 0
          applyFromScroll()
        })
    }
    applyFromScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [pinned, applyFromScroll, applyProgress])

  const textBlock = (
    <>
      <p className="type-label-md text-white/50 uppercase">
        {t(k.femaleHormonesMriEyebrow)}
      </p>
      <h2 className="type-display-lg text-neutral-50">
        {t(k.femaleHormonesMriHeading)}
      </h2>
      <p className="type-title-lg max-w-[560px] font-medium text-white">
        {t(k.femaleHormonesMriBody)}
      </p>
    </>
  )

  const cardsBlock = (
    // md+ uses two shared rows via subgrid — tiles, then captions — so every
    // tile gives back the same height and their bottoms stay aligned even
    // though the captions wrap to different depths. The row gap is dropped
    // there because the caption carries its own mt-4. On the static mobile
    // branch each card is a plain flex column at the natural 4:5 ratio.
    <div className="mx-auto grid min-h-0 w-full max-w-[960px] shrink grid-cols-1 gap-5 md:grid-cols-3 md:grid-rows-[minmax(0,1fr)_auto] md:gap-y-0">
      {findings.map((f) => (
        <div
          key={f.titleKey}
          className="flex min-h-0 flex-col md:row-span-2 md:grid md:grid-rows-subgrid"
        >
          {/* 4:5 is the tile's natural size and its ceiling: it sets the row's
              max-content height, and the grid above only shrinks, so a tall
              viewport stops here. md:h-full makes the height definite so the
              tile actually follows the row when it shrinks — aspect-ratio
              alone outranks stretch and would overflow instead. Short
              viewports therefore crop the scan (object-cover) rather than
              push the caption and the CTA row past the bottom of the stage. */}
          <div className="relative aspect-[4/5] min-h-0 w-full overflow-hidden rounded-[24px] bg-neutral-950 md:h-full">
            {/* Ahead-generated reading-room composite (clinician + workstation
                screen). Illustrative scan, NOT a real patient's study. Kept in
                colour so the warm reading room reads reassuring rather than
                clinical. See the section comment above. */}
            <Image
              src={f.image}
              alt=""
              fill
              sizes="(max-width: 768px) 100vw, 320px"
              className={`scale-[0.85] object-cover contrast-110 ${f.imgClass}`}
            />
            {/* progressive blur, ramping from the middle down to the bottom, so
                the lower half of the scan softens (reads less clinical/graphic)
                and the chip sits on a calm surface. */}
            <div
              aria-hidden
              className="absolute inset-x-0 top-1/3 bottom-0 backdrop-blur-md"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to bottom, transparent, black 55%)",
                maskImage: "linear-gradient(to bottom, transparent, black 55%)",
              }}
            />
            {/* dark gradient to seat the reading chip */}
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            <div className="absolute inset-x-3 bottom-3 z-10 md:inset-x-4 md:bottom-4">
              <ReadingChip
                title={t(f.titleKey)}
                statusLabel=""
                findings={f.chip}
              />
            </div>
          </div>
          {/* description — the finding name (white) leads the sentence as the
              card heading, the rest continues in a more transparent white.
              `shrink-0` keeps it readable in full when the tile above it is
              giving height back. */}
          <p className="mt-4 min-h-[3.5rem] shrink-0 text-lg leading-snug font-medium text-white/70">
            <span className="text-white">{t(f.titleKey)}</span> {t(f.noteKey)}
          </p>
        </div>
      ))}
    </div>
  )

  // Soft secondary link — the MRI is the upsell, not the primary offer, so it
  // hands off to the pricing section rather than opening booking directly.
  const ctaBlock = (
    <a
      href="#pricing"
      className="type-body-lg group inline-flex h-[52px] items-center justify-center gap-2 rounded-full border border-white/40 px-10 font-medium text-white transition-colors hover:bg-white/10"
    >
      {t(k.femaleHormonesMriCta)}
      <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
    </a>
  )

  // Full-bleed MRI photo behind the section — a woman patient with a clinician
  // at the scanner (page-specific asset; the shared metabolism mri-suite shot
  // read as male-default here).
  const bgSrc = blobMediaUrl("/images/marketing/female-hormones-mri-room.jpg")
  const bg = (
    <div aria-hidden className="absolute inset-0">
      <div
        className="absolute inset-0"
        style={{ opacity: pinned ? "var(--sharp, 1)" : "0" }}
      >
        <Image
          src={bgSrc}
          alt=""
          fill
          sizes="100vw"
          className="scale-[1.18] object-cover object-[50%_18%]"
        />
      </div>
      <div
        className="absolute inset-0"
        style={{ opacity: pinned ? "calc(1 - var(--sharp, 1))" : "1" }}
      >
        <Image
          src={bgSrc}
          alt=""
          fill
          sizes="100vw"
          className="scale-[1.18] object-cover object-[50%_18%] blur-xl"
        />
      </div>
      {/* Legibility scrim: darker overall, and darkest through the vertical
          middle band where the centred heading sits (the previous via-/20 left
          it too light and the patient's lit face bled through the text). */}
      <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/70 via-neutral-950/50 to-neutral-950/75" />
      {/* As the cards drive in (--op 0→1) the whole background darkens toward
          near-black, so the dark finding cards read against a calmer field
          instead of the bright blurred photo. Pinned-only (var default 0). */}
      <div
        aria-hidden
        className="absolute inset-0 bg-neutral-950"
        style={{ opacity: "calc(var(--op, 0) * 0.55)" }}
      />
    </div>
  )

  // Mobile: no pin, no reveal — a plain centred stack, everything visible.
  if (!pinned) {
    return (
      <section className="relative overflow-hidden bg-neutral-900 py-20 text-white">
        {bg}
        <div className={`${SECTION} relative z-10 w-full`}>
          <div className="mx-auto flex max-w-[760px] flex-col items-center gap-5 text-center">
            {textBlock}
          </div>
          <div className="mt-10">{cardsBlock}</div>
          <div className="mt-10 flex justify-center">{ctaBlock}</div>
        </div>
      </section>
    )
  }

  // Desktop/tablet: pinned track with two overlapping centred layers — the
  // header rides up and out, the cards rise in to replace it.
  return (
    <div
      ref={trackRef}
      className="relative w-screen"
      style={{
        height: "240vh",
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
      }}
    >
      <div
        ref={stageRef}
        className="sticky top-0 h-screen w-full overflow-hidden bg-neutral-900 text-white"
      >
        {bg}
        {/* Two overlapping layers, both bounded to the stage (inset-0) so
            neither can spill past 100vh. The header rides up and out as --sub
            and --head fall, which is what frees the height for the cards; the
            cards then have the whole stage to lay out in. */}
        <div className="relative h-full">
          {/* Header layer — centred at rest, slides up and out on scroll. The
              eyebrow + body line fade on the way (opacity only, as before). */}
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-center"
            style={{
              opacity: "var(--head, 1)",
              transform: "translateY(calc(var(--reveal, 0) * -60vh))",
            }}
          >
            <div className={`${SECTION} w-full`}>
              <div className="mx-auto flex max-w-[760px] flex-col items-center gap-5 text-center">
                <p
                  className="type-label-md text-white/50 uppercase"
                  style={{ opacity: "var(--sub, 1)" }}
                >
                  {t(k.femaleHormonesMriEyebrow)}
                </p>
                <h2 className="type-display-lg text-neutral-50">
                  {t(k.femaleHormonesMriHeading)}
                </h2>
                <p
                  className="type-title-lg max-w-[560px] font-medium text-white"
                  style={{ opacity: "var(--sub, 1)" }}
                >
                  {t(k.femaleHormonesMriBody)}
                </p>
              </div>
            </div>
          </div>
          {/* Cards layer — fades in as the header leaves and gets the full
              stage height. Top padding clears the fixed navbar pill (36px
              offset + 72px tall). The grid inside only shrinks, never grows, so
              on a tall viewport the tiles stop at their natural 4:5 and
              justify-center recentres the block instead of stretching it. */}
          <div
            className={`${SECTION} [pointer-events:var(--cards-pe,none)] absolute inset-0 z-10 flex flex-col justify-center pt-[7.25rem] pb-8`}
            style={{ opacity: "var(--op, 0)" }}
          >
            {cardsBlock}
            <div className="mt-6 flex shrink-0 justify-center">{ctaBlock}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
